/**
 * Glitter nail designs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_nails_shades`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const glitterNailDesignsPack: ToilePack = {
    slug: 'glitter-nail-designs',
    category: 'beauty',
    name: 'Glitter nail designs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'fade', label: 'Gold fade', subject: 'nude nails with gold glitter fading from the cuticle'},
        {key: 'full', label: 'Full silver', subject: 'short square nails fully coated in silver glitter'},
        {key: 'french', label: 'Glitter French', subject: 'almond nails with glitter French tips'},
    ],
    styles: [
        {key: 'newyear', label: 'New Year', style: 'at a New Year party, warm soft glow from camera-left, a dark festive balance with the glitter sparkling against the night', palette: 'Black and gold with a few scattered confetti', framing: 'A near-frontal close view, the fingers around a champagne coupe'},
        {key: 'disco', label: 'Disco', style: 'under the scattered sparkle of a mirror ball from above, a dark balance pricked with small points of brightness', palette: 'Deep navy shadow and silver specks across skin and nails', framing: 'A near-frontal close view, the fingers spread slightly'},
        {key: 'daylight', label: 'Daylight', style: 'on a white linen sheet in soft daylight from camera-right, a bright clean balance that shows every glitter fleck', palette: 'White linen, the glitter colours the only colour', framing: 'From directly above, the hand flat in the centre of a vertical frame'},
    ],
    directions: ['rose gold', 'red glitter', 'toe nails'],
    directionHint: 'try: rose gold',
    seo: {
        title: 'Glitter Nail Designs: Gold, Silver, French',
        metaDescription: 'Nine glitter nail pictures: a gold fade, full silver and glitter French tips, shown at a party, on a disco night and in daylight.',
        h1: 'Glitter nail designs: gold fade, full silver, glitter French',
        tagline: 'Nine pictures of glitter nails, each design shown at a New Year party, under a mirror ball and in daylight.',
        intro: 'Glitter nails are for a party or the New Year, when the sparkle has to show. The nine pictures show nude nails with gold glitter fading from the cuticle, short square nails fully coated in silver, and almond nails with glitter French tips. Each design appears at a party, under a mirror ball and in plain daylight. Type a different colour or a twist, such as rose gold or red glitter, and the same settings carry it.',
        useCases: [
            "a New Year's Eve manicure",
            'a party or wedding guest nail idea',
            'a disco or themed night out',
            'a reference for a nail appointment',
        ],
        faq: [
            {q: 'What are the three glitter designs?', a: 'A gold fade from the cuticle on nude nails, a full silver glitter coat on short square nails, and a glitter French tip on almond nails.'},
            {q: 'What does the daylight setting show?', a: 'The glitter on a white linen sheet in soft daylight, so each fleck of glitter is easy to see.'},
            {q: 'Can I ask for a different colour?', a: 'Yes. Type it as a request, such as rose gold. The picture is not promised to match it exactly.'},
            {q: 'Are these photographs of real nails?', a: 'No. They are generated images of one hand in each setting, not photographs of a particular person.'},
        ],
    },
}
